/**
 * Villager trading (vanilla Java VillagerTrades / MerchantOffer), DOM-free.
 *
 *  - Professions come from a claimed workstation block (WORKSTATIONS).
 *  - Each level (Novice..Master) adds 2 offers picked at random from that
 *    level's pool (vanilla addOffersFromItemListings with 2).
 *  - An offer: cost A (+ optional cost B) -> result, `maxUses` before it locks,
 *    villager XP per use, price multiplier for the demand adjustment.
 *  - Restock (at the workstation, at most twice a day): uses -> 0 and demand
 *    updated (demand += uses - (maxUses - uses), floored at 0); the price of
 *    cost A is base + max(0, floor(base * mult * demand)).
 *  - Villager level thresholds: 10 / 70 / 150 / 250 XP (Apprentice..Master).
 *  - The player gets 3-6 XP per trade (vanilla 3 + nextInt(4)).
 *
 * Trades whose items this game does not have are left out of the pools
 * (pumpkin pie, cake, ink sacs, name tags, fish, bells, ...), so a few
 * professions have thin or empty pools at some levels. Reputation / gossip
 * discounts and hero-of-the-village are not implemented.
 */

import * as B from '../blocks';
import { I } from './itemIds';
import { rollEnchantments } from './enchant/table';
import type { Ench, Stack } from './types';

export const PROFESSIONS = [
  'none', 'nitwit', 'farmer', 'librarian', 'fletcher', 'fisherman', 'toolsmith', 'weaponsmith', 'armorer', 'butcher', 'mason',
  // appended: saved indices stay stable. Their job-site blocks (cartography table, cauldron, loom) are not in the game yet.
  'cleric', 'leatherworker', 'cartographer', 'shepherd',
] as const;
export type Profession = typeof PROFESSIONS[number];

/** Biome villager types (texture overlay entity/villager/type/<name>.png). Index is saved. */
export const VILLAGER_TYPES = ['plains', 'desert', 'savanna', 'taiga', 'snow', 'jungle', 'swamp'] as const;

export const LEVEL_NAMES = ['Novice', 'Apprentice', 'Journeyman', 'Expert', 'Master'] as const;
/** XP needed to reach level 1..5 (index = level - 1). */
export const LEVEL_XP = [0, 10, 70, 150, 250] as const;

/** Workstation block id -> profession. */
export const WORKSTATIONS: ReadonlyMap<number, Profession> = new Map<number, Profession>([
  [B.COMPOSTER_ID, 'farmer'], [B.LECTERN_ID, 'librarian'], [B.FLETCHING_TABLE_ID, 'fletcher'],
  [B.BARREL_ID, 'fisherman'], [B.SMITHING_TABLE_ID, 'toolsmith'], [B.GRINDSTONE_ID, 'weaponsmith'],
  [B.BLAST_FURNACE_ID, 'armorer'], [B.SMOKER_ID, 'butcher'], [B.STONECUTTER_ID, 'mason'],
  [B.BREWING_STAND_ID, 'cleric'],
]);

export const professionIndex = (p: Profession): number => Math.max(0, PROFESSIONS.indexOf(p));

/** Level (1..5) for an XP total. */
export function levelForXp(xp: number): number {
  let l = 1;
  for (let i = 1; i < LEVEL_XP.length; i++) if (xp >= LEVEL_XP[i]) l = i + 1;
  return l;
}

/** XP needed for the next level, or -1 at Master. */
export const nextLevelXp = (level: number): number => (level >= 5 ? -1 : LEVEL_XP[level]);

// ------------------------------------------------------------------ tables

interface Tpl {
  a: readonly [number, number];
  b?: readonly [number, number];
  out: readonly [number, number];
  uses: number;
  xp: number;
  mult: number;
  /** Result is enchanted (vanilla EnchantedItemForEmeralds): +level emeralds, random 5..19 level enchant. */
  ench?: boolean;
}

const blk = (name: string): number => B.BLOCKS.find((b) => b?.name === name)?.id ?? 0;
const E = I.EMERALD;
const sell = (id: number, n: number, emeralds: number, uses: number, xp: number, mult = 0.05): Tpl =>
  ({ a: [E, emeralds], out: [id, n], uses, xp, mult });
const buy = (id: number, n: number, uses: number, xp: number, mult = 0.05): Tpl =>
  ({ a: [id, n], out: [E, 1], uses, xp, mult });
const ench = (id: number, emeralds: number, xp: number): Tpl =>
  ({ a: [E, emeralds], out: [id, 1], uses: 3, xp, mult: 0.2, ench: true });

/** Pools per profession per level (index 0 = Novice). */
export const TRADE_TABLES: Partial<Record<Profession, Tpl[][]>> = {
  farmer: [
    [buy(I.WHEAT, 20, 16, 2), buy(I.POTATO, 26, 16, 2), buy(I.CARROT, 22, 16, 2), buy(I.BEETROOT, 15, 16, 2), sell(I.BREAD, 6, 1, 16, 1)],
    [buy(blk('Pumpkin'), 6, 12, 10), sell(I.APPLE, 4, 1, 16, 5)],
    [sell(I.COOKIE, 18, 3, 12, 10), buy(blk('Melon'), 4, 12, 20)],
    [],
    [sell(I.GOLDEN_CARROT, 3, 3, 12, 30)],
  ],
  librarian: [
    [buy(I.PAPER, 24, 16, 2), sell(B.BOOKSHELF_ID, 1, 9, 12, 1)],
    [buy(I.BOOK, 4, 12, 10)],
    [sell(blk('Glass'), 4, 1, 12, 10)],
    [],
    [sell(I.NAME_TAG, 1, 20, 12, 30)],
  ],
  fletcher: [
    [buy(I.STICK, 32, 16, 2), sell(I.ARROW, 16, 1, 12, 1), { a: [E, 1], b: [blk('Gravel'), 10], out: [I.FLINT, 10], uses: 12, xp: 1, mult: 0.05 }],
    [buy(I.FLINT, 26, 12, 10), sell(I.BOW, 1, 2, 12, 5)],
    [buy(I.STRING, 14, 16, 20), sell(I.CROSSBOW, 1, 3, 12, 10)],
    [buy(I.FEATHER, 24, 16, 30), ench(I.BOW, 2, 15)],
    [ench(I.CROSSBOW, 3, 30)],
  ],
  fisherman: [
    [buy(I.STRING, 20, 16, 2), buy(I.COAL, 10, 16, 2)],
    [], [], [], [],
  ],
  toolsmith: [
    [buy(I.COAL, 15, 16, 2), sell(I.STONE_AXE, 1, 1, 12, 1), sell(I.STONE_SHOVEL, 1, 1, 12, 1), sell(I.STONE_PICKAXE, 1, 1, 12, 1), sell(I.STONE_HOE, 1, 1, 12, 1)],
    [buy(I.IRON_INGOT, 4, 12, 10)],
    [buy(I.FLINT, 30, 12, 20), ench(I.IRON_AXE, 1, 10), ench(I.IRON_SHOVEL, 2, 10), ench(I.IRON_PICKAXE, 3, 10), sell(I.DIAMOND_HOE, 1, 4, 3, 10, 0.2)],
    [buy(I.DIAMOND, 1, 12, 30), ench(I.DIAMOND_AXE, 12, 15), ench(I.DIAMOND_SHOVEL, 5, 15)],
    [ench(I.DIAMOND_PICKAXE, 13, 30)],
  ],
  weaponsmith: [
    [buy(I.COAL, 15, 16, 2), sell(I.IRON_AXE, 1, 3, 12, 1, 0.2), ench(I.IRON_SWORD, 2, 1)],
    [buy(I.IRON_INGOT, 4, 12, 10), sell(I.IRON_SPEAR, 1, 4, 12, 5, 0.2)], // spear drop: iron spear (est. price)
    [buy(I.FLINT, 24, 12, 20)],
    [buy(I.DIAMOND, 1, 12, 30), ench(I.DIAMOND_AXE, 12, 15)],
    [ench(I.DIAMOND_SWORD, 8, 30)],
  ],
  armorer: [
    [buy(I.COAL, 15, 16, 2), sell(I.IRON_LEGGINGS, 1, 7, 12, 1, 0.2), sell(I.IRON_BOOTS, 1, 4, 12, 1, 0.2), sell(I.IRON_HELMET, 1, 5, 12, 1, 0.2), sell(I.IRON_CHESTPLATE, 1, 9, 12, 1, 0.2)],
    [buy(I.IRON_INGOT, 4, 12, 10)],
    [buy(I.DIAMOND, 1, 12, 20), sell(I.SHIELD, 1, 5, 12, 10, 0.2)],
    [ench(I.DIAMOND_LEGGINGS, 14, 15), ench(I.DIAMOND_BOOTS, 8, 15)],
    [ench(I.DIAMOND_HELMET, 8, 30), ench(I.DIAMOND_CHESTPLATE, 16, 30)],
  ],
  butcher: [
    [buy(I.CHICKEN, 14, 16, 2), buy(I.PORKCHOP, 7, 16, 2)],
    [buy(I.COAL, 15, 16, 2), sell(I.COOKED_PORKCHOP, 5, 1, 16, 5), sell(I.COOKED_CHICKEN, 8, 1, 16, 5)],
    [buy(I.MUTTON, 7, 16, 20), buy(I.BEEF, 10, 16, 20)],
    [buy(blk('Dried Kelp Block'), 10, 12, 30)],
    [],
  ],
  mason: [
    [buy(I.CLAY_BALL, 10, 16, 2), sell(I.BRICK, 10, 1, 16, 1)],
    [buy(B.STONE_ID, 20, 16, 10), sell(blk('Chiseled Stone Bricks'), 4, 1, 16, 5)],
    [buy(blk('Granite'), 16, 16, 20), buy(blk('Andesite'), 16, 16, 20), buy(blk('Diorite'), 16, 16, 20),
      sell(blk('Polished Andesite'), 4, 1, 16, 10), sell(blk('Polished Diorite'), 4, 1, 16, 10), sell(blk('Polished Granite'), 4, 1, 16, 10)],
    [buy(I.QUARTZ, 12, 12, 30), sell(blk('White Terracotta'), 1, 1, 12, 15)],
    [sell(blk('Quartz Block'), 1, 1, 12, 30)],
  ],
  cleric: [
    [buy(I.ROTTEN_FLESH, 32, 16, 2), sell(I.REDSTONE, 2, 1, 12, 1)],
    [buy(I.GOLD_INGOT, 3, 12, 10), sell(I.LAPIS_LAZULI, 1, 1, 12, 5)],
    [buy(I.RABBIT_FOOT, 2, 12, 20), sell(I.GLOWSTONE_DUST, 4, 1, 12, 10)],
    [buy(I.GLASS_BOTTLE, 9, 12, 30), sell(I.ENDER_PEARL, 1, 5, 12, 15)],
    [buy(blk('Nether Wart'), 22, 12, 30)],
  ],
  leatherworker: [
    [buy(I.LEATHER, 6, 16, 2), sell(I.LEATHER_LEGGINGS, 1, 3, 12, 1, 0.2)],
    [buy(I.FLINT, 26, 12, 10), sell(I.LEATHER_BOOTS, 1, 2, 12, 5, 0.2)],
    [buy(I.RABBIT_FOOT, 9, 12, 20), sell(I.LEATHER_CHESTPLATE, 1, 7, 12, 10, 0.2)],
    [buy(I.TURTLE_SCUTE, 4, 12, 30), sell(I.LEATHER_HELMET, 1, 2, 12, 15, 0.2)],
    [sell(I.SADDLE, 1, 6, 12, 30)],
  ],
  cartographer: [
    [buy(I.PAPER, 24, 16, 2)],
    [], [], [], [],
  ],
};

// strip trades that reference items/blocks this build lacks
for (const levels of Object.values(TRADE_TABLES)) {
  if (!levels) continue;
  for (let i = 0; i < levels.length; i++) levels[i] = levels[i].filter((t) => t.a[0] > 0 && t.out[0] > 0 && (!t.b || t.b[0] > 0));
}

// ------------------------------------------------------------------ offers

export interface Offer {
  a: Stack;
  b: Stack | null;
  out: Stack;
  uses: number;
  maxUses: number;
  xp: number;
  mult: number;
  demand: number;
}

const st = (id: number, count: number): Stack => ({ id, count, dmg: 0 });

function fromTpl(t: Tpl, rnd: () => number): Offer {
  const out = st(t.out[0], t.out[1]);
  let price = t.a[1];
  if (t.ench) {
    // vanilla EnchantedItemForEmeralds: enchant at level 5..19, cost += level (max 64)
    const lvl = 5 + Math.floor(rnd() * 15);
    const list: Ench[] = rollEnchantments(Math.floor(rnd() * 0x7fffffff), 0, out.id, lvl);
    if (list.length) out.ench = list.map((e) => ({ ...e }));
    price = Math.min(64, price + lvl);
  }
  return { a: st(t.a[0], price), b: t.b ? st(t.b[0], t.b[1]) : null, out, uses: 0, maxUses: t.uses, xp: t.xp, mult: t.mult, demand: 0 };
}

/** The 2 new offers a villager of `prof` gets on reaching `level` (1..5). */
export function rollOffers(prof: Profession, level: number, rnd: () => number): Offer[] {
  const pool = TRADE_TABLES[prof]?.[level - 1];
  if (!pool || pool.length === 0) return [];
  const idx = pool.map((_, i) => i);
  const out: Offer[] = [];
  while (out.length < 2 && idx.length) {
    const k = Math.floor(rnd() * idx.length);
    out.push(fromTpl(pool[idx[k]], rnd));
    idx.splice(k, 1);
  }
  return out;
}

/** Price of cost A with the demand adjustment (vanilla MerchantOffer.getCostA). */
export function priceA(o: Offer): number {
  const base = o.a.count;
  const adj = Math.max(0, Math.floor(base * o.mult * o.demand));
  return Math.max(1, Math.min(64, base + adj));
}

export const outOfStock = (o: Offer): boolean => o.uses >= o.maxUses;

/** Do the payment stacks satisfy offer `o` (matching ids, enough count)? */
export function satisfies(o: Offer, pa: Stack, pb: Stack): boolean {
  if (outOfStock(o)) return false;
  const needA = priceA(o);
  const okA = (s: Stack): boolean => s.id === o.a.id && s.count >= needA;
  const okB = (s: Stack): boolean => !o.b || (s.id === o.b.id && s.count >= o.b.count);
  const emptyB = (s: Stack): boolean => !o.b || s.count <= 0 || s.id === 0;
  return (okA(pa) && okB(pb)) || (!!o.b && okA(pb) && okB(pa)) || (!o.b && okA(pa) && emptyB(pb)) || (!o.b && okA(pb) && (pa.count <= 0 || pa.id === 0));
}

/** Take the payment out of (pa, pb) for one use. Call only when `satisfies`. */
export function takePayment(o: Offer, pa: Stack, pb: Stack): void {
  const needA = priceA(o);
  const take = (s: Stack, n: number): void => { s.count -= n; if (s.count <= 0) { s.count = 0; s.id = 0; s.dmg = 0; delete s.ench; } };
  if (pa.id === o.a.id && pa.count >= needA && (!o.b || (pb.id === o.b.id && pb.count >= o.b.count))) {
    take(pa, needA); if (o.b) take(pb, o.b.count);
  } else if (pb.id === o.a.id && pb.count >= needA) {
    take(pb, needA); if (o.b) take(pa, o.b.count);
  } else take(pa, needA);
}

/** A fresh copy of the result stack. */
export const resultOf = (o: Offer): Stack => ({ ...o.out, ench: o.out.ench?.map((e) => ({ ...e })) });

/** Record one use; returns the villager XP gained. */
export function useOffer(o: Offer): number { o.uses++; return o.xp; }

/** Player XP for one trade (vanilla 3 + nextInt(4)). */
export const playerTradeXp = (rnd: () => number): number => 3 + Math.floor(rnd() * 4);

/** Restock (vanilla Villager.restock + updateDemand). */
export function restock(offers: Offer[]): void {
  for (const o of offers) {
    o.demand = Math.max(0, o.demand + o.uses - (o.maxUses - o.uses));
    o.uses = 0;
  }
}

export const needsRestock = (offers: readonly Offer[]): boolean => offers.some((o) => o.uses > 0);

// ------------------------------------------------------------------ persistence

/** [aId, aCount, bId, bCount, outId, outCount, uses, maxUses, xp, mult, demand, ench?] */
export type OfferRow = [number, number, number, number, number, number, number, number, number, number, number, Ench[]?];

export function offerToRow(o: Offer): OfferRow {
  const r: OfferRow = [o.a.id, o.a.count, o.b?.id ?? 0, o.b?.count ?? 0, o.out.id, o.out.count, o.uses, o.maxUses, o.xp, o.mult, o.demand];
  if (o.out.ench?.length) r.push(o.out.ench);
  return r;
}

export function offerFromRow(r: unknown): Offer | null {
  if (!Array.isArray(r) || r.length < 11) return null;
  const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const out = st(n(r[4]), n(r[5]));
  if (Array.isArray(r[11])) out.ench = (r[11] as Ench[]).map((e) => ({ ...e }));
  return {
    a: st(n(r[0]), n(r[1])), b: n(r[2]) > 0 ? st(n(r[2]), n(r[3])) : null, out,
    uses: n(r[6]), maxUses: n(r[7]) || 1, xp: n(r[8]), mult: n(r[9]), demand: n(r[10]),
  };
}
